import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";

import { api, ApiError } from "../lib/api.js";
import { useLiveFeed } from "../lib/live.js";
import { encodeQr, qrToSvg } from "../lib/qr.js";
import { dictionaryFor } from "../lib/i18n.js";
import { nextItem, orderPlaylist } from "../lib/wall-playlist.js";
import { clampWallDuration, normalizeSlideTransition, type Media, type WallPayload } from "@shared";

/** How long a freshly arrived photograph is outlined. */
const ARRIVAL_HIGHLIGHT_MS = 12_000;

/**
 * The wall chrome sits directly on the host's chosen background, and hosts may
 * pick anything from cream to charcoal. Pick dark or light ink from the
 * background's luminance instead of hardcoding white, which vanished on the
 * default cream theme.
 */
function readableInk(hex: string): "#1b1420" | "#ffffff" {
  const match = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!match) return "#ffffff";
  const value = Number.parseInt(match[1]!, 16);
  const red = (value >> 16) & 0xff;
  const green = (value >> 8) & 0xff;
  const blue = value & 0xff;
  const luminance = (0.299 * red + 0.587 * green + 0.114 * blue) / 255;
  return luminance > 0.58 ? "#1b1420" : "#ffffff";
}

/**
 * The QR card on the wall.
 *
 * Black on white with a quiet zone, whatever the event theme is: a projector
 * washes out contrast and a tinted QR is a QR that does not scan. The SVG is
 * generated by our own encoder and contains no user text — the caption beside
 * it is rendered as a normal text node, never as markup.
 *
 * It is not a link. A television has no pointer, and an anchor would only add a
 * focusable object nobody in the room can reach.
 */
function WallQr({
  svg,
  caption,
  guestUrl,
  primaryColor,
  empty,
}: {
  svg: string;
  caption: string;
  guestUrl: string;
  primaryColor: string;
  empty?: boolean;
}) {
  if (!svg) return null;

  return (
    <aside
      className={`wall-qr${empty ? " wall-qr-empty" : ""}`}
      aria-label="QR code to open the guest gallery"
      data-guest-url={guestUrl}
      style={{ borderColor: primaryColor }}
    >
      {/*
        `qrToSvg` returns a complete, static SVG string built from the encoder's
        module matrix. There is no event-supplied content in it, and parsing it
        as markup is what keeps the card a single scalable image rather than a
        React tree of a thousand rects.
      */}
      <div className="wall-qr-code" dangerouslySetInnerHTML={{ __html: svg }} />
      <p className="wall-qr-caption">{caption}</p>
    </aside>
  );
}

/**
 * The projector surface.
 *
 * Everything here is in service of one constraint: this runs on a television, on
 * a USB stick, watched from across a room by people who are not holding a
 * remote. So there are no controls, no text small enough to need squinting at,
 * and nothing that changes on its own — the grid is whatever the last guests
 * have sent, and it stays that way until someone sends something new.
 *
 * The EventSource reconnects by itself (see lib/live.ts), so a dropped Wi-Fi
 * link on the venue's own router heals without anybody noticing.
 */
export function Wall() {
  const { slug } = useParams<{ slug: string }>();
  const [params] = useSearchParams();
  const token = params.get("k") ?? undefined;

  /**
   * The wall already holds its own credential. When media is served through the
   * Worker (the local/default path), the `<img>` request cannot carry a header,
   * so pass the wall token along. This also keeps a password-protected event's
   * projector working without asking the television for the guest password.
   */
  const mediaSrc = useCallback(
    (url: string): string => {
      if (!token || !url.startsWith("/api/media/")) return url;
      return `${url}${url.includes("?") ? "&" : "?"}k=${encodeURIComponent(token)}`;
    },
    [token],
  );

  const [payload, setPayload] = useState<WallPayload | null>(null);
  const [error, setError] = useState("");
  const [arrivals, setArrivals] = useState<Record<string, number>>({});
  const [musPlaying, setMusPlaying] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const arrivalTimers = useRef<ReturnType<typeof setTimeout>[]>([]);

  useEffect(() => {
    if (!slug) return;

    api
      .get<WallPayload>(`/api/wall/${slug}${token ? `?k=${token}` : ""}`)
      .then((result) => {
        setPayload(result);
        setError("");
      })
      .catch((err) =>
        setError(
          err instanceof ApiError && err.status === 401
            ? "This wall needs its link. Open the wall URL from the host dashboard."
            : "This wall is not available.",
        ),
      );
  }, [slug, token]);

  const theme = payload?.event.theme;

  const photoDurationMs = clampWallDuration(theme?.slideDurationMs, 8_000, 3_000, 30_000);
  const videoDurationMs = clampWallDuration(theme?.videoSlideDurationMs, 30_000, 3_000, 120_000);

  /**
   * Highlight a new arrival for twelve seconds, then forget it.
   *
   * The expiry has to be explicit. `Date.now()` inside render only re-evaluates
   * when something re-renders the page, so on a wall where the last upload
   * arrived at 11:58 and nothing else happens for twenty minutes, that one
   * photograph would keep its border for the rest of the evening.
   */
  const noteArrival = useCallback((item: Media) => {
    setArrivals((prev) => ({ ...prev, [item.mediaId]: Date.now() }));

    const timer = setTimeout(() => {
      setArrivals((prev) => {
        if (!(item.mediaId in prev)) return prev;
        const next = { ...prev };
        delete next[item.mediaId];
        return next;
      });
    }, ARRIVAL_HIGHLIGHT_MS);

    arrivalTimers.current.push(timer);
  }, []);

  // Timers outlive the component unless they are cleared; a projector left on a
  // wall all night would otherwise accumulate one pending timer per upload.
  useEffect(
    () => () => {
      for (const timer of arrivalTimers.current) clearTimeout(timer);
      arrivalTimers.current = [];
    },
    [],
  );

  const { status, media, removed } = useLiveFeed({
    // Only connect once the wall payload has actually loaded. Opening the feed
    // on a wall whose token is missing produced a stream that 401s on every
    // attempt, forever: a projector left pointed at a stale URL would then
    // retry against the Worker indefinitely, which is exactly the situation
    // where nobody is present to notice or stop it.
    slug: payload ? (slug ?? null) : null,
    token,
    onMedia: noteArrival,
  });

  // The first paint comes from the database, because the Durable Object's ring
  // buffer dies with its isolate. Anything the feed delivers afterwards is
  // merged on top; the feed alone would leave a projector that connects an hour
  // after the last upload showing an empty screen.
  const items = useMemo(() => {
    const merged = new Map<string, Media>();
    for (const item of media) merged.set(item.mediaId, item);
    for (const item of payload?.media ?? []) if (!merged.has(item.mediaId)) merged.set(item.mediaId, item);
    // The first paint is a snapshot. A photo the host deletes or rejects after
    // it was taken must leave the wall too, not linger until a reload.
    for (const id of removed) merged.delete(id);
    return [...merged.values()].sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  }, [media, payload, removed]);

  /* ------------------------------------------------------------------ */
  /* Slideshow                                                          */
  /* ------------------------------------------------------------------ */

  const isSlideshow = theme?.wallMode === "slideshow";
  const ordered = useMemo(() => orderPlaylist(items), [items]);
  const [currentId, setCurrentId] = useState<string | null>(null);
  const [paused, setPaused] = useState(false);
  const [tabHidden, setTabHidden] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);
  const [outgoing, setOutgoing] = useState<Media | null>(null);
  const [controlsVisible, setControlsVisible] = useState(true);

  /**
   * The live list, readable from timers without making every timer depend on
   * the list's identity. A new photograph must join the rotation *without*
   * resetting the clock on the current slide; depending on `ordered` in the
   * timer effect would do exactly the thing the feature forbids.
   */
  const orderedRef = useRef(ordered);
  const itemsByIdRef = useRef(new Map<string, Media>());
  const previousIdRef = useRef<string | null>(null);
  const lastIndexRef = useRef(0);
  const controlsTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    orderedRef.current = ordered;
    itemsByIdRef.current = new Map(ordered.map((item) => [item.mediaId, item]));
  }, [ordered]);

  // Keep the slide valid. If it was deleted or rejected, move to whatever now
  // occupies its slot (or the last slot, if the list shrank); if nothing is
  // left, fall back to the empty state.
  useEffect(() => {
    if (ordered.length === 0) {
      lastIndexRef.current = 0;
      setCurrentId(null);
      return;
    }

    const index = currentId ? ordered.findIndex((item) => item.mediaId === currentId) : -1;
    if (index !== -1) {
      lastIndexRef.current = index;
      return;
    }

    const nextIndex = Math.min(lastIndexRef.current, ordered.length - 1);
    setCurrentId(ordered[nextIndex]!.mediaId);
  }, [ordered, currentId]);

  const currentItem = useMemo(
    () => ordered.find((item) => item.mediaId === currentId) ?? null,
    [ordered, currentId],
  );
  const currentIndex = currentItem ? ordered.indexOf(currentItem) : -1;
  const currentIsVideo = currentItem?.mimeType.startsWith("video/") ?? false;
  const canRotate = ordered.length > 1;

  useEffect(() => {
    const query = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = (): void => setReducedMotion(query.matches);
    update();
    query.addEventListener?.("change", update);
    return () => query.removeEventListener?.("change", update);
  }, []);

  useEffect(() => {
    const update = (): void => setTabHidden(document.visibilityState === "hidden");
    update();
    document.addEventListener("visibilitychange", update);
    return () => document.removeEventListener("visibilitychange", update);
  }, []);

  /**
   * One timer, keyed on the slide and the pause state.
   *
   * `canRotate` changes only when the list crosses one item, so a new upload
   * while the show is running does not restart the dwell. Videos hand over when
   * their clip ends, with the configured maximum as a safety fallback.
   */
  useEffect(() => {
    if (!isSlideshow || paused || tabHidden || !canRotate || !currentId) return;

    const delay = currentIsVideo ? Math.max(3_000, videoDurationMs) : Math.max(3_000, photoDurationMs);
    const timer = setTimeout(() => {
      const next = nextItem(orderedRef.current, currentId, 1);
      if (next) setCurrentId(next.mediaId);
    }, delay);

    return () => clearTimeout(timer);
  }, [isSlideshow, paused, tabHidden, canRotate, currentId, currentIsVideo, photoDurationMs, videoDurationMs]);

  /**
   * Keep the previous photograph on screen for the length of the transition.
   *
   * Videos switch directly: a crossfade that freezes a video's last frame and
   * fades it out is worse than a cut. Reduced motion cuts too, but the rotation
   * itself continues — the transition is decoration, the rotation is the show.
   */
  useEffect(() => {
    const previousId = previousIdRef.current;
    previousIdRef.current = currentId;

    setOutgoing(null);
    if (!currentId || !previousId || previousId === currentId || !isSlideshow || reducedMotion) {
      return;
    }

    const previous = itemsByIdRef.current.get(previousId);
    const current = itemsByIdRef.current.get(currentId);
    if (!previous || !current) return;
    if (previous.mimeType.startsWith("video/") || current.mimeType.startsWith("video/")) return;

    setOutgoing(previous);
    const timer = setTimeout(() => setOutgoing(null), 520);
    return () => clearTimeout(timer);
  }, [currentId, isSlideshow, reducedMotion]);

  const advance = useCallback(
    (direction: 1 | -1): void => {
      const next = nextItem(orderedRef.current, currentId, direction);
      if (next) setCurrentId(next.mediaId);
    },
    [currentId],
  );

  const showControls = useCallback((): void => {
    setControlsVisible(true);
    if (controlsTimerRef.current) clearTimeout(controlsTimerRef.current);
    if (paused) return;
    controlsTimerRef.current = setTimeout(() => setControlsVisible(false), 3_000);
  }, [paused]);

  useEffect(() => {
    if (!isSlideshow) return;
    showControls();
    return () => {
      if (controlsTimerRef.current) clearTimeout(controlsTimerRef.current);
    };
  }, [isSlideshow, showControls]);

  // Pointer, touch and remote all bring the controls back. A projector should
  // not carry a permanent toolbar, but a remote must be able to find one.
  useEffect(() => {
    if (!isSlideshow) return;
    const wake = (): void => showControls();
    window.addEventListener("mousemove", wake);
    window.addEventListener("touchstart", wake);
    window.addEventListener("keydown", wake);
    return () => {
      window.removeEventListener("mousemove", wake);
      window.removeEventListener("touchstart", wake);
      window.removeEventListener("keydown", wake);
    };
  }, [isSlideshow, showControls]);

  useEffect(() => {
    if (!isSlideshow) return;

    const onKey = (event: KeyboardEvent): void => {
      const target = event.target as HTMLElement | null;
      if (target && /^(INPUT|TEXTAREA|SELECT|BUTTON|A)$/.test(target.tagName)) return;

      if (event.key === " " || event.code === "Space") {
        event.preventDefault();
        setPaused((value) => !value);
        showControls();
        return;
      }
      if (event.key === "ArrowRight") {
        event.preventDefault();
        advance(1);
        showControls();
        return;
      }
      if (event.key === "ArrowLeft") {
        event.preventDefault();
        advance(-1);
        showControls();
      }
    };

    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isSlideshow, advance, showControls]);

  // Decode the next still while the current one is on screen. Without this the
  // first frame of every new slide is a blank rectangle on venue Wi-Fi.
  useEffect(() => {
    if (!isSlideshow) return;
    const next = nextItem(orderedRef.current, currentId, 1);
    if (!next || next.mediaId === currentId || next.mimeType.startsWith("video/")) return;

    const image = new Image();
    image.src = mediaSrc(next.thumbUrl || next.displayUrl);
    void image.decode?.().catch(() => undefined);
  }, [isSlideshow, currentId, mediaSrc]);

  // The QR carries the *guest* link, not the gallery: the wall's job is to turn
  // occupants into uploaders, and /u/<code> is the card the printed sheets use
  // too. Falls back to the gallery path when the payload predates guestPath.
  const guestUrl = payload
    ? new URL(
        payload.guestPath || `/e/${payload.event.slug}`,
        window.location.origin,
      ).toString()
    : "";
  const qrSvg = useMemo(() => {
    if (!payload?.event.theme.showQrOnWall || !guestUrl) return "";
    try {
      return qrToSvg(encodeQr(guestUrl), 4);
    } catch {
      // A URL too long for the encoder is better without a QR than with a
      // truncated one that scans to the wrong event.
      return "";
    }
  }, [payload?.event.theme.showQrOnWall, guestUrl]);

  // Guest-facing words follow the event's language. The setup messages above
  // ("needs its link") are for whoever is plugging in the projector and stay
  // English with the rest of the host tooling.
  const copy = dictionaryFor(payload?.event.guestLanguage ?? "");
  const qrCaption = theme?.qrCaption || copy.wallScan;

  /**
   * Only genuinely new photographs get the arrival outline.
   *
   * A reconnect can replay the Durable Object's buffer, and on a wall that has
   * been up for two hours that is thirty photographs re-announcing themselves at
   * once — the whole screen flaring for no reason. The feed dedupes against its
   * own list, but the first paint arrives separately, so a replayed item that is
   * already on screen has to be filtered here too.
   */
  const highlight = useMemo(() => {
    const known = new Set<string>();
    const fresh = new Set<string>();
    // Newest first, same order the grid renders in, so the first time an id is
    // seen is the first time the visitor could have seen it.
    for (const item of items) {
      if (known.has(item.mediaId)) continue;
      known.add(item.mediaId);
      if (item.mediaId in arrivals) fresh.add(item.mediaId);
    }
    return fresh;
  }, [items, arrivals]);

  // Phase 7.5. Off unless the host both supplied a track and allowed playback,
  // because a projector that starts making noise on its own at a wedding is not
  // a feature.
  const musicUrl =
    payload?.event.allowBackgroundMusic && theme?.backgroundMusicUrl ? theme.backgroundMusicUrl : null;

  useEffect(() => {
    if (!musicUrl) return;
    const audio = new Audio(musicUrl);
    audio.loop = true;
    audio.volume = 0.4;
    audioRef.current = audio;

    // Browsers refuse to autoplay without a user gesture. A kiosk that has never
    // been touched simply stays silent, and the button below is how somebody
    // starts it.
    void audio.play().then(
      () => setMusPlaying(true),
      () => setMusPlaying(false),
    );

    return () => {
      audio.pause();
      audioRef.current = null;
    };
  }, [musicUrl]);

  /**
   * Start or stop the track.
   *
   * This handler is reachable at all only because the button is rendered
   * whenever a track exists. An earlier version rendered it conditionally on
   * `musPlaying`, which meant the one case that needed it — autoplay refused —
   * was the one case with no control on screen. The host's only way to start
   * the music was the one gesture that was not available.
   */
  const toggleMusic = (): void => {
    const audio = audioRef.current;
    if (!audio) return;

    if (!audio.paused) {
      audio.pause();
      setMusPlaying(false);
      return;
    }

    // The rejection is deliberately swallowed: a second click after a refusal
    // should do nothing visible rather than throw, and `play()` is the same
    // promise that already failed once.
    void audio.play().then(
      () => setMusPlaying(true),
      () => setMusPlaying(false),
    );
  };

  if (error) {
    return (
      <div className="fullscreen wall-plain">
        <p className="wall-message">{error}</p>
      </div>
    );
  }

  if (!payload) {
    return (
      <div className="fullscreen wall-plain">
        <p className="wall-message">Loading the wall…</p>
      </div>
    );
  }

  const showNames = payload.event.theme.showGuestNames;
  const showCaptions = payload.event.theme.showCaptionsOnWall;
  // White chrome disappears on the default cream wall; pick ink for the theme.
  const ink = readableInk(payload.event.theme.backgroundColor);

  const transition = normalizeSlideTransition(payload.event.theme.slideTransition);

  const renderSlide = (item: Media, layer: "current" | "outgoing") => {
    const video = item.mimeType.startsWith("video/");
    const motionClass =
      layer === "outgoing"
        ? `wall-slide-out-${transition}`
        : outgoing
          ? `wall-slide-in-${transition}`
          : "";

    return (
      <figure
        key={`${layer}-${item.mediaId}`}
        data-media-id={item.mediaId}
        className={`wall-slide wall-slide-${layer}${motionClass ? ` ${motionClass}` : ""}`}
      >
        {video ? (
          <video
            // Slideshow videos play once and hand over. Looping here would pin
            // the entire show to the first guest who sent a ten-second clip.
            src={mediaSrc(item.displayUrl)}
            poster={mediaSrc(item.thumbUrl)}
            muted
            autoPlay={layer === "current"}
            playsInline
            preload="metadata"
            onEnded={layer === "current" ? () => advance(1) : undefined}
            onError={layer === "current" ? () => advance(1) : undefined}
          />
        ) : (
          <img
            src={mediaSrc(item.thumbUrl || item.displayUrl)}
            alt={item.caption || item.aiCaption || ""}
          />
        )}

        {item.blurred ? <span className="wall-blurred">Hidden by the host</span> : null}

        {layer === "current" && ((showCaptions && item.caption) || (showNames && item.guestName)) ? (
          <figcaption className="wall-slide-caption">
            {showCaptions && item.caption ? <strong>{item.caption}</strong> : null}
            {showNames && item.guestName ? <span>{item.guestName}</span> : null}
          </figcaption>
        ) : null}
      </figure>
    );
  };

  /*
   * The wrapper carries `wall-layout-grid` / `wall-layout-masonry`; the grid
   * container inside it is plain `.wall-grid`.
   *
   * Those two names used to both be "wall-grid". The wrapper picked up
   * `display: grid` and `grid-template-columns` from what was meant to be only a
   * modifier, which overrode `.wall`'s `display: flex` on source order — so the
   * real grid became a single grid *item* sitting in column one, and the
   * projector showed one narrow column of squashed photographs with most of the
   * screen empty.
   *
   * Nothing threw, every test passed, and it looked correct in any screenshot
   * with fewer than about five photos. Found by rendering it.
   */
  return (
    <div
      className={`fullscreen wall wall-layout-${payload.event.theme.layout}${
        isSlideshow ? " wall-mode-slideshow" : ""
      }`}
      style={{
        backgroundColor: payload.event.theme.backgroundColor,
        color: ink,
        ...(payload.event.theme.backgroundImageUrl
          ? {
              backgroundImage: `url(${payload.event.theme.backgroundImageUrl})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
            }
          : {}),
      }}
    >
      {/*
        The indicator is deliberately almost invisible. A projector wall that
        shows a reconnection spinner in front of guests is worse than one that
        quietly waits.
      */}
      <span className={`wall-status wall-status-${status}`} aria-live="polite" />

      {isSlideshow ? (
        ordered.length === 0 ? (
          <div className="wall-empty">
            <h1>{payload.event.title}</h1>
            <p>{qrSvg ? copy.wallFirstPhoto : copy.wallWaiting}</p>
            <WallQr
              svg={qrSvg}
              caption={qrCaption}
              guestUrl={guestUrl}
              primaryColor={payload.event.theme.primaryColor}
              empty
            />
          </div>
        ) : (
          <div className="wall-slideshow">
            {/*
              Two layers only while a transition is running. The outgoing
              photograph keeps its place underneath so the screen never flashes
              the background between slides.
            */}
            {outgoing ? renderSlide(outgoing, "outgoing") : null}
            {currentItem ? renderSlide(currentItem, "current") : null}
          </div>
        )
      ) : items.length === 0 ? (
        <div className="wall-empty">
          <h1>{payload.event.title}</h1>
          <p>{qrSvg ? copy.wallFirstPhoto : copy.wallWaiting}</p>
          <WallQr
            svg={qrSvg}
            caption={qrCaption}
            guestUrl={guestUrl}
            primaryColor={payload.event.theme.primaryColor}
            empty
          />
        </div>
      ) : (
        <div className="wall-grid">
          {items.map((item) => (
            <figure
              key={item.mediaId}
              data-media-id={item.mediaId}
              className={`wall-tile${item.pinned ? " wall-tile-pinned" : ""}`}
              style={
                highlight.has(item.mediaId)
                  ? { borderColor: payload.event.theme.primaryColor }
                  : undefined
              }
            >
              {item.mimeType.startsWith("audio/") ? (
                <div className="wall-audio-tile">
                  <audio src={mediaSrc(item.displayUrl)} controls preload="metadata" />
                </div>
              ) : item.mimeType.startsWith("video/") ? (
                <video
                  src={mediaSrc(item.displayUrl)}
                  poster={mediaSrc(item.thumbUrl)}
                  muted
                  autoPlay
                  loop
                  playsInline
                  preload="metadata"
                />
              ) : (
                <img
                  // A projector wall is the surface most punished by originals:
                  // thirty 4 MB photos is 120 MB over venue Wi-Fi. The 720px
                  // derivative is indistinguishable from across a room.
                  src={mediaSrc(item.thumbUrl || item.displayUrl)}
                  alt={item.caption || item.aiCaption || ""}
                  loading="lazy"
                />
              )}

              {/* A moderated photo stays on the wall but is covered: the host
                  chose to show it with a warning, not to hide it. */}
              {item.blurred ? <span className="wall-blurred">Hidden by the host</span> : null}

              {showNames && item.guestName ? <figcaption>{item.guestName}</figcaption> : null}
            </figure>
          ))}
        </div>
      )}

      {isSlideshow && ordered.length > 0 ? (
        <div
          className={`wall-controls${controlsVisible ? " wall-controls-visible" : ""}`}
          role="group"
          aria-label="Slideshow controls"
        >
          <button
            type="button"
            aria-label="Previous photo"
            onClick={() => {
              advance(-1);
              showControls();
            }}
          >
            ‹
          </button>
          <button
            type="button"
            aria-pressed={paused}
            onClick={() => {
              setPaused((value) => !value);
              showControls();
            }}
          >
            {paused ? "Play" : "Pause"}
          </button>
          <button
            type="button"
            aria-label="Next photo"
            onClick={() => {
              advance(1);
              showControls();
            }}
          >
            ›
          </button>
          <span className="wall-controls-label">
            Slideshow · {Math.max(0, currentIndex) + 1} / {ordered.length}
          </span>
        </div>
      ) : null}

      {items.length > 0 ? (
        <WallQr
          svg={qrSvg}
          caption={qrCaption}
          guestUrl={guestUrl}
          primaryColor={payload.event.theme.primaryColor}
        />
      ) : null}

      <div className="wall-corner">
        <span className="wall-title">{payload.event.title}</span>
        <span className="wall-count">
          {items.length}
          {payload.totalCount > items.length ? ` of ${payload.totalCount}` : ""}
        </span>
        {musicUrl ? (
          <button
            type="button"
            className="wall-music"
            aria-pressed={musPlaying}
            onClick={toggleMusic}
          >
            {musPlaying ? "Music on — tap to stop" : "Music paused — tap to play"}
          </button>
        ) : null}
      </div>
    </div>
  );
}
